// Step 6 — Test on real loads, then publish (Shadow or Live). Also the version history and Pause / Resume.
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { InfoCircle, TickCircle, Warning2 } from 'iconsax-react';

import useApi from 'hooks/useApi';
import { diApi } from '../../api';
import type { PipelineDetail, PlanKind, TestRunResult } from '../../types';
import type { StepProps } from '../stepProps';
import { MONO, PanelHead, SubCard, errMsg, formatDateTime, unwrap } from './SharedParts';

const DAYS = [1, 3, 7, 14];

const PLAN_LABEL: Record<PlanKind, string> = {
  CREATE: 'Would create',
  ADOPT: 'Link to existing',
  FLAG: 'Already tracked · flag',
  UPDATE: 'Would update',
  NOOP: 'No change',
  CANCEL: 'Would cancel',
  SKIP: 'Skipped',
  HOLD: 'Held for review'
};
const PLAN_COLOR: Record<PlanKind, 'success' | 'info' | 'warning' | 'default' | 'error'> = {
  CREATE: 'success',
  ADOPT: 'info',
  FLAG: 'warning',
  UPDATE: 'info',
  NOOP: 'default',
  CANCEL: 'error',
  SKIP: 'default',
  HOLD: 'warning'
};

// Spec snapshot of the test each pipeline last ran in this session — survives the step unmounting.
const testedSpecs = new Map<string, { testUid: string; specKey: string }>();

const Tile = ({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) => (
  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="h3" sx={{ mt: 0.5 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    )}
  </Paper>
);

const IssueIcon = ({ level }: { level: 'warning' | 'ok' | 'error' }) =>
  level === 'ok' ? (
    <TickCircle size={18} color="currentColor" variant="Bold" />
  ) : level === 'error' ? (
    <InfoCircle size={18} color="currentColor" variant="Bold" />
  ) : (
    <Warning2 size={18} color="currentColor" variant="Bold" />
  );

export default function TestPublishStep(props: StepProps) {
  const { clientUid, pipeline, spec, processes, flushDraft, onPipelineUpdated } = props;
  const { showToast } = useApi();
  const toastErr = (e: any, fallback: string) => showToast(errMsg(e, fallback), 'error');

  const [days, setDays] = useState(7);
  const [testing, setTesting] = useState(false);
  const [mode, setMode] = useState<'SHADOW' | 'LIVE'>('SHADOW');
  const [backfill, setBackfill] = useState(false);
  const [backfillDays, setBackfillDays] = useState(7);
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [toggling, setToggling] = useState(false);

  const result: TestRunResult | null = pipeline.lastTest || null;
  const specKey = JSON.stringify(spec);
  // Latest values for async handlers (flushDraft may replace the spec / pipeline while a call is in flight).
  const specKeyRef = useRef(specKey);
  specKeyRef.current = specKey;
  const pipelineRef = useRef(pipeline);
  pipelineRef.current = pipeline;

  // Baseline for "changed since the test": the spec the test ran on (this session), else the spec when first seen.
  const baseline = useRef<string>(specKey);
  useEffect(() => {
    if (!result) return;
    const known = testedSpecs.get(pipeline.uid);
    if (!known || known.testUid !== result.uid) testedSpecs.set(pipeline.uid, { testUid: result.uid, specKey: baseline.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline.uid, result?.uid]);
  const tested = testedSpecs.get(pipeline.uid);
  const dirtySinceTest = !!result && (tested && tested.testUid === result.uid ? tested.specKey !== specKey : baseline.current !== specKey);

  const errors = (pipeline.draftValidation || []).filter((v) => v.level === 'error');
  const canPublish = !!result && !dirtySinceTest && errors.length === 0 && !testing && !publishing;
  const nextVersion = Math.max(pipeline.draftVersion || 1, (pipeline.liveVersion || 0) + 1);
  const processName = (uid: string | null) => (uid ? processes.find((p) => p.uid === uid)?.name || 'Unknown process' : null);

  const runTest = async () => {
    setTesting(true);
    setConfirming(false);
    try {
      if (flushDraft) await flushDraft();
      const r = unwrap(await diApi.test(clientUid, pipeline.uid, { days }));
      testedSpecs.set(pipeline.uid, { testUid: r.uid, specKey: specKeyRef.current });
      // The server stored the result as lastTest; re-read so draftValidation etc. are fresh too.
      let fresh: PipelineDetail = { ...pipelineRef.current, lastTest: r };
      try {
        const p = unwrap(await diApi.getPipeline(clientUid, pipeline.uid));
        if (p?.lastTest?.uid === r.uid) fresh = p;
      } catch {
        // keep the local merge
      }
      onPipelineUpdated?.(fresh);
    } catch (e) {
      toastErr(e, 'The test could not run');
    } finally {
      setTesting(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      if (flushDraft) await flushDraft();
      const p = unwrap(
        await diApi.publish(clientUid, pipeline.uid, {
          mode,
          note: note.trim() || undefined,
          backfillDays: backfill ? backfillDays : undefined
        })
      );
      onPipelineUpdated?.(p);
      setConfirming(false);
      setNote('');
      setBackfill(false);
      showToast(`Published v${p.liveVersion ?? nextVersion} ${mode === 'LIVE' ? 'live' : 'in Shadow mode'}`, 'success');
    } catch (e) {
      toastErr(e, 'Could not publish');
    } finally {
      setPublishing(false);
    }
  };

  const pauseOrResume = async (action: 'pause' | 'resume') => {
    setToggling(true);
    try {
      const p = unwrap(await (action === 'pause' ? diApi.pause(clientUid, pipeline.uid) : diApi.resume(clientUid, pipeline.uid)));
      onPipelineUpdated?.(p);
      showToast(action === 'pause' ? 'Pipeline paused. Records keep queueing.' : 'Pipeline resumed', 'success');
    } catch (e) {
      toastErr(e, action === 'pause' ? 'Could not pause' : 'Could not resume');
    } finally {
      setToggling(false);
    }
  };

  const perDay = result && result.days ? Math.max(1, Math.round(result.matched / result.days)) : 0;
  const confirmText = useMemo(() => {
    if (!result) return null;
    const back = backfill ? ` It will also run on the matching loads from the last ${backfillDays} day${backfillDays > 1 ? 's' : ''}.` : '';
    return mode === 'LIVE' ? (
      <>
        <b>Publish v{nextVersion} live?</b> It will create and update transactions for matching loads — the test would have created{' '}
        {result.plans.CREATE || 0} over {result.days} day{result.days > 1 ? 's' : ''} — flag loads that are already tracked, and run the
        rules.{back}
      </>
    ) : (
      <>
        <b>Publish v{nextVersion} in Shadow mode?</b> About {perDay} load{perDay === 1 ? '' : 's'} a day will run through it. It creates
        nothing and notifies nobody.{back}
      </>
    );
  }, [result, mode, backfill, backfillDays, nextVersion, perDay]);

  const versions = [...(pipeline.versions || [])].sort((a, b) => b.version - a.version);
  const state = pipeline.state;

  return (
    <Stack spacing={3}>
      <PanelHead
        title="Test on real loads, then publish"
        subtitle="The test runs this draft over loads we've already received. It creates nothing and sends nothing."
      />

      {(state === 'SHADOW' || state === 'LIVE' || state === 'PAUSED' || state === 'NEEDS_ATTENTION') && (
        <Alert
          severity={state === 'LIVE' ? 'success' : state === 'SHADOW' ? 'info' : 'warning'}
          action={
            state === 'SHADOW' || state === 'LIVE' ? (
              <Button color="inherit" size="small" disabled={toggling} onClick={() => pauseOrResume('pause')}>
                Pause
              </Button>
            ) : (
              <Button color="inherit" size="small" disabled={toggling} onClick={() => pauseOrResume('resume')}>
                Resume
              </Button>
            )
          }
        >
          {state === 'LIVE' && `v${pipeline.liveVersion ?? '?'} is running live.`}
          {state === 'SHADOW' &&
            `v${pipeline.liveVersion ?? '?'} is running in Shadow mode — it records what would happen and creates nothing.`}
          {state === 'PAUSED' &&
            `Paused${pipeline.healthMessage ? ` — ${pipeline.healthMessage}` : ''}. Records keep queueing, so nothing is lost.`}
          {state === 'NEEDS_ATTENTION' &&
            `Needs attention${pipeline.healthMessage ? ` — ${pipeline.healthMessage}` : ''}. Records keep queueing, so nothing is lost.`}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="di-test-days">Test on</InputLabel>
          <Select labelId="di-test-days" label="Test on" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {DAYS.map((d) => (
              <MenuItem key={d} value={d}>
                Last {d} day{d > 1 ? 's' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={runTest}
          disabled={testing}
          startIcon={testing ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {testing ? 'Running…' : 'Run test'}
        </Button>
      </Stack>

      {!result && !testing && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, borderStyle: 'dashed', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Not tested yet. Publishing needs a passing test.
          </Typography>
        </Paper>
      )}

      {result && (
        <Stack spacing={3}>
          {dirtySinceTest && <Alert severity="warning">You changed the pipeline after this test. Run it again before publishing.</Alert>}
          <Typography variant="caption" color="text.secondary">
            Tested {formatDateTime(result.ranAt)} over {result.total.toLocaleString('en-US')} loads from the last {result.days} day
            {result.days > 1 ? 's' : ''}.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 1.25 }}>
            <Tile label="Matched" value={result.matched.toLocaleString('en-US')} />
            <Tile
              label="Would create"
              value={result.plans.CREATE || 0}
              sub={result.plans.HOLD ? `${result.plans.HOLD} held for review` : undefined}
            />
            <Tile
              label="Already tracked · flag"
              value={result.plans.FLAG || 0}
              sub={result.plans.ADOPT ? `+${result.plans.ADOPT} linked to existing` : undefined}
            />
            <Tile
              label="Would update"
              value={result.plans.UPDATE || 0}
              sub={result.plans.NOOP ? `${result.plans.NOOP} unchanged` : undefined}
            />
            <Tile label="Excluded" value={result.excluded.toLocaleString('en-US')} />
          </Box>

          <Stack spacing={1}>
            <Typography variant="h5">Check before publishing</Typography>
            {result.issues.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nothing to check.
              </Typography>
            )}
            {result.issues.map((iss, i) => (
              <Stack
                key={i}
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: iss.level === 'ok' ? 'success.light' : iss.level === 'error' ? 'error.light' : 'warning.light',
                  bgcolor: iss.level === 'ok' ? 'success.lighter' : iss.level === 'error' ? 'error.lighter' : 'warning.lighter',
                  color: iss.level === 'ok' ? 'success.dark' : iss.level === 'error' ? 'error.dark' : 'warning.dark'
                }}
              >
                <Box sx={{ pt: 0.25, display: 'flex' }}>
                  <IssueIcon level={iss.level} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {iss.message}
                  {typeof iss.count === 'number' ? ` (${iss.count.toLocaleString('en-US')})` : ''}
                </Typography>
              </Stack>
            ))}
            {errors.length > 0 && (
              <Alert severity="error">
                Fix {errors.length} problem{errors.length > 1 ? 's' : ''} in the draft before publishing:{' '}
                {errors.map((e) => e.message).join(' · ')}
              </Alert>
            )}
          </Stack>

          {result.byRoute && result.byRoute.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="h5">By route</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Value</TableCell>
                      <TableCell align="right">Loads</TableCell>
                      <TableCell>Sent to</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.byRoute.map((r) => (
                      <TableRow key={r.value}>
                        <TableCell sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>{r.value || '—'}</TableCell>
                        <TableCell align="right">{r.count}</TableCell>
                        <TableCell>
                          {r.teamUid ? processName(r.teamUid) : <Chip size="small" color="warning" variant="outlined" label="Not routed" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 2 }}>
            <Stack spacing={1}>
              <Typography variant="h5">Sample matched loads</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Load</TableCell>
                      <TableCell>Summary</TableCell>
                      <TableCell>Would</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.sampleMatched.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            No loads matched.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {result.sampleMatched.map((m) => (
                      <TableRow key={m.recordId}>
                        <TableCell sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>{m.recordId}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{m.summary}</Typography>
                          {m.note && (
                            <Typography variant="caption" color="text.secondary">
                              {m.note}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={PLAN_COLOR[m.plan]} variant="outlined" label={PLAN_LABEL[m.plan] || m.plan} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="h5">Sample excluded loads</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Load</TableCell>
                      <TableCell>Why</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.sampleExcluded.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="text.secondary">
                            No loads were excluded.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {result.sampleExcluded.map((x) => (
                      <TableRow key={x.recordId}>
                        <TableCell sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>{x.recordId}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{x.reason}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Box>

          <SubCard title="Publish as">
            <Stack spacing={2}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {(['SHADOW', 'LIVE'] as const).map((m) => {
                  const on = mode === m;
                  return (
                    <Paper
                      key={m}
                      variant="outlined"
                      onClick={() => {
                        setMode(m);
                        setConfirming(false);
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        borderColor: on ? 'primary.main' : 'divider',
                        borderWidth: on ? 2 : 1,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-start'
                      }}
                    >
                      <Radio
                        size="small"
                        checked={on}
                        value={m}
                        inputProps={{ 'aria-label': m === 'SHADOW' ? 'Shadow' : 'Live' }}
                        sx={{ p: 0.5 }}
                      />
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle1">{m === 'SHADOW' ? 'Shadow' : 'Live'}</Typography>
                          {m === 'SHADOW' && <Chip size="small" color="info" label="Recommended" sx={{ height: 20 }} />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {m === 'SHADOW'
                            ? 'Runs everything and records what would happen. Creates nothing and notifies nobody.'
                            : 'Creates and updates transactions, and runs the rules.'}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={<Checkbox size="small" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} />}
                  label={<Typography variant="body2">Also run it on matching loads from the last</Typography>}
                />
                <TextField
                  size="small"
                  type="number"
                  value={backfillDays}
                  disabled={!backfill}
                  onChange={(e) => setBackfillDays(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                  inputProps={{ 'aria-label': 'Backfill days', min: 1, max: 30 }}
                  sx={{ width: 80 }}
                />
                <Typography variant="body2">days</Typography>
              </Stack>
              <TextField
                size="small"
                label="Version note"
                placeholder="What changed and why"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
              />
              {confirming ? (
                <Alert
                  severity={mode === 'LIVE' ? 'warning' : 'info'}
                  action={
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" size="small" onClick={publish} disabled={!canPublish}>
                        {publishing ? 'Publishing…' : `Publish v${nextVersion}`}
                      </Button>
                      <Button size="small" color="inherit" onClick={() => setConfirming(false)} disabled={publishing}>
                        Cancel
                      </Button>
                    </Stack>
                  }
                >
                  {confirmText}
                </Alert>
              ) : (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button variant="contained" disabled={!canPublish} onClick={() => setConfirming(true)}>
                    Publish v{nextVersion}
                  </Button>
                  {!canPublish && !testing && (
                    <Typography variant="caption" color="text.secondary">
                      {dirtySinceTest ? 'Run the test again first.' : errors.length ? 'Fix the draft problems first.' : ''}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </SubCard>
        </Stack>
      )}

      <SubCard title="Versions">
        {versions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing published yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Published</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((v) => (
                  <TableRow key={v.version}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">v{v.version}</Typography>
                        {v.version === pipeline.liveVersion && (
                          <Chip size="small" color="success" variant="outlined" label="Running" sx={{ height: 20 }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={v.mode === 'LIVE' ? 'success' : 'info'}
                        label={v.mode === 'LIVE' ? 'Live' : 'Shadow'}
                        sx={{ height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDateTime(v.publishedAt)}</Typography>
                      {v.publishedBy && (
                        <Typography variant="caption" color="text.secondary">
                          {v.publishedBy}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={v.note ? 'text.primary' : 'text.disabled'}>
                        {v.note || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SubCard>
    </Stack>
  );
}
